import { Dispatch } from 'redux';
import { actions as baseActions } from '../../context';
import { Services } from 'types/services';

const IS_VIEW_ADDING_NEW_COMMUNICATION = 'IS_VIEW_ADDING_NEW_COMMUNICATION';
const TOGGLE_ASSIGN_USER_TO_PRACTICE_MODAL = 'TOGGLE_ASSIGN_USER_TO_PRACTICE_MODAL';
const TOGGLE_EDITING_PRACTICE = 'TOGGLE_EDITING_PRACTICE';
const TOGGLE_EDIT_CONVERSION_PIMS_MODAL = 'TOGGLE_EDIT_CONVERSION_PIMS_MODAL';
const TOGGLE_EDIT_PRACTICE_FORM_EDITING = 'TOGGLE_EDIT_PRACTICE_FORM_EDITING';
const TOGGLE_IGNORE_FAILED_SYNC_MODAL = 'TOGGLE_IGNORE_FAILED_SYNC_MODAL';
const TOGGLE_IS_RELATIONS_MODAL_OPEN = 'TOGGLE_IS_RELATIONS_MODAL_OPEN';
const TOGGLE_LEADS_GENERATION_CONFIRMATION_MODAL = 'TOGGLE_LEADS_GENERATION_CONFIRMATION_MODAL';
const TOGGLE_ADD_NEW_PLUGIN_OFFERING_MODAL = 'TOGGLE_ADD_NEW_PLUGIN_OFFERING_MODAL';
const TOGGLE_CONNECT_PLUGIN_DETAIL_MODAL = 'TOGGLE_CONNECT_PLUGIN_DETAIL_MODAL';
const TOGGLE_PRACTICE_CONFIG_MODAL = 'TOGGLE_PRACTICE_CONFIG_MODAL';
const TOGGLE_PRACTICE_REFRESH = 'TOGGLE_PRACTICE_REFRESH';
const TOGGLE_PRACTICE_WORKFLOW_MODAL = 'TOGGLE_PRACTICE_WORKFLOW_MODAL';
const TOGGLE_REFRESH = 'TOGGLE_REFRESH';
const TOGGLE_REFRESH_ACTIVITIES = 'TOGGLE_REFRESH_ACTIVITIES';
const TOGGLE_RESET_CONVERSION_MODAL = 'TOGGLE_RESET_CONVERSION_MODAL';
const TOGGLE_SCHEDULE_MODAL = 'TOGGLE_SCHEDULE_MODAL';
const TOGGLE_SIDEBAR_OPEN = 'TOGGLE_SIDEBAR_OPEN';
const TOGGLE_TEXT_PRACTICE_SEARCH = 'TOGGLE_TEXT_PRACTICE_SEARCH';
const TOGGLE_UPDATE_NOTES_MODAL = 'TOGGLE_UPDATE_NOTES_MODAL';

interface State {
  isResetConversionModalOpen: boolean;
  isEditConversionPimsModalOpen: boolean;
  isEditingPractice: boolean;
  isMutePracticeModalOpen: boolean;
  isAssignUserToPracticeModalOpen: boolean;
  isPracticeScheduleModalOpen: boolean;
  isPracticeWorkflowModalOpen: boolean;
  isRelationsModalOpen: boolean;
  isConnectConfigModalOpen: boolean;
  isSidebarOpen: boolean;
  isSidebarPracticeSearchOpen: boolean;
  isUpdateNotesModalOpen: boolean;
  isLeadsGenerationConfirmationModalOpen: boolean;
  isAddNewPluginOfferingModalOpen: boolean;
  isConnectPluginDetailModalOpen: boolean;
  isViewAddingNewCommunication: Record<string, boolean>;
  triggerRefresh: boolean;
  triggerPracticeRefresh: boolean;
  triggerActivitiesRefresh: boolean;
}

const initialState: State = {
  isResetConversionModalOpen: false,
  isEditConversionPimsModalOpen: false,
  isEditingPractice: false,
  isMutePracticeModalOpen: false,
  isAssignUserToPracticeModalOpen: false,
  isPracticeScheduleModalOpen: false,
  isPracticeWorkflowModalOpen: false,
  isRelationsModalOpen: false,
  isConnectConfigModalOpen: false,
  isSidebarOpen: true,
  isSidebarPracticeSearchOpen: false,
  isUpdateNotesModalOpen: false,
  isLeadsGenerationConfirmationModalOpen: false,
  isAddNewPluginOfferingModalOpen: false,
  isConnectPluginDetailModalOpen: false,
  isViewAddingNewCommunication: {},
  triggerRefresh: false,
  triggerPracticeRefresh: false,
  triggerActivitiesRefresh: false,
};

interface Action {
  type: string;
  payload?: unknown;
}

export const reducer = (state = initialState, action: Action): State => {
  switch (action.type) {
    case TOGGLE_RESET_CONVERSION_MODAL:
      return { ...state, isResetConversionModalOpen: action.payload as boolean };
    case TOGGLE_EDIT_CONVERSION_PIMS_MODAL:
      return { ...state, isEditConversionPimsModalOpen: action.payload as boolean };
    case TOGGLE_EDIT_PRACTICE_FORM_EDITING:
      return {
        ...state,
        isEditingPractice: (action.payload as { isEditing: boolean }).isEditing,
      };
    case TOGGLE_PRACTICE_CONFIG_MODAL:
      return { ...state, isConnectConfigModalOpen: action.payload as boolean };
    case TOGGLE_IS_RELATIONS_MODAL_OPEN:
      return { ...state, isRelationsModalOpen: action.payload as boolean };
    case TOGGLE_IGNORE_FAILED_SYNC_MODAL:
      return {
        ...state,
        isMutePracticeModalOpen: (action.payload as { isOpen: boolean }).isOpen,
      };
    case TOGGLE_ASSIGN_USER_TO_PRACTICE_MODAL:
      return {
        ...state,
        isAssignUserToPracticeModalOpen: (action.payload as { isOpen: boolean }).isOpen,
      };
    case IS_VIEW_ADDING_NEW_COMMUNICATION: {
      const { practiceId, status } = action.payload as { practiceId: string; status: boolean };
      return {
        ...state,
        isViewAddingNewCommunication: {
          ...state.isViewAddingNewCommunication,
          [practiceId]: status,
        },
      };
    }
    case TOGGLE_REFRESH:
      return { ...state, triggerRefresh: !state.triggerRefresh };
    case TOGGLE_PRACTICE_REFRESH:
      return { ...state, triggerPracticeRefresh: !state.triggerPracticeRefresh };
    case TOGGLE_REFRESH_ACTIVITIES:
      return { ...state, triggerActivitiesRefresh: !state.triggerActivitiesRefresh };
    case TOGGLE_SCHEDULE_MODAL:
      return { ...state, isPracticeScheduleModalOpen: action.payload as boolean };
    case TOGGLE_UPDATE_NOTES_MODAL:
      return { ...state, isUpdateNotesModalOpen: action.payload as boolean };
    case TOGGLE_EDITING_PRACTICE:
      return { ...state, isEditingPractice: action.payload as boolean };
    case TOGGLE_SIDEBAR_OPEN:
      return { ...state, isSidebarOpen: action.payload as boolean };
    case TOGGLE_TEXT_PRACTICE_SEARCH:
      return { ...state, isSidebarPracticeSearchOpen: action.payload as boolean };
    case TOGGLE_PRACTICE_WORKFLOW_MODAL:
      return { ...state, isPracticeWorkflowModalOpen: action.payload as boolean };
    case TOGGLE_LEADS_GENERATION_CONFIRMATION_MODAL:
      return { ...state, isLeadsGenerationConfirmationModalOpen: action.payload as boolean };
    case TOGGLE_ADD_NEW_PLUGIN_OFFERING_MODAL:
      return { ...state, isAddNewPluginOfferingModalOpen: action.payload as boolean };
    case TOGGLE_CONNECT_PLUGIN_DETAIL_MODAL:
      return { ...state, isConnectPluginDetailModalOpen: action.payload as boolean };
    default:
      return state;
  }
};

export const actions = (_services?: Services) => {
  const toggleResetConversionModalOpen = (status: boolean) => ({
    type: TOGGLE_RESET_CONVERSION_MODAL,
    payload: status,
  });

  const toggleEditConversionPimsModalOpen = (status: boolean) => ({
    type: TOGGLE_EDIT_CONVERSION_PIMS_MODAL,
    payload: status,
  });

  const toggleEditingPractice = (status: boolean) => ({
    type: TOGGLE_EDIT_PRACTICE_FORM_EDITING,
    payload: { isEditing: status },
  });

  const toggleConnectConfigModal = (status: boolean) => ({
    type: TOGGLE_PRACTICE_CONFIG_MODAL,
    payload: status,
  });

  const toggleRelationsModalOpen = (status: boolean) => ({
    type: TOGGLE_IS_RELATIONS_MODAL_OPEN,
    payload: status,
  });

  const toggleMutePracticeModal = (status: boolean) => ({
    type: TOGGLE_IGNORE_FAILED_SYNC_MODAL,
    payload: { isOpen: status },
  });

  const toggleAssignUserToPracticeModal = (status: boolean) => ({
    type: TOGGLE_ASSIGN_USER_TO_PRACTICE_MODAL,
    payload: { isOpen: status },
  });

  const toggleIsViewAddingNewCommunication = (payload: {
    practiceId: string;
    status: boolean;
  }) => ({
    type: IS_VIEW_ADDING_NEW_COMMUNICATION,
    payload,
  });

  const triggerRefresh = () => {
    return async (dispatch: Dispatch) => {
      baseActions.practice.getDashboardStats()(dispatch);
      dispatch({ type: TOGGLE_REFRESH });
    };
  };

  const triggerPracticeRefresh = () => ({
    type: TOGGLE_PRACTICE_REFRESH,
  });

  const activitiesRefresh = () => ({
    type: TOGGLE_REFRESH_ACTIVITIES,
  });

  const togglePracticeScheduleModal = (status = false) => ({
    type: TOGGLE_SCHEDULE_MODAL,
    payload: status,
  });

  const toggleUpdateNotesModal = (status = false) => ({
    type: TOGGLE_UPDATE_NOTES_MODAL,
    payload: status,
  });

  const toggleIsEditingPractice = (status = false) => ({
    type: TOGGLE_EDITING_PRACTICE,
    payload: status,
  });

  const toggleSidebarOpen = (status: boolean) => ({
    type: TOGGLE_SIDEBAR_OPEN,
    payload: status,
  });

  const toggleSidebarPracticeSearchOpen = (status: boolean) => ({
    type: TOGGLE_TEXT_PRACTICE_SEARCH,
    payload: status,
  });

  const togglePracticeWorkflowModalOpen = (status: boolean) => ({
    type: TOGGLE_PRACTICE_WORKFLOW_MODAL,
    payload: status,
  });

  const toggleLeadsGenerationConfirmationModal = (status: boolean) => ({
    type: TOGGLE_LEADS_GENERATION_CONFIRMATION_MODAL,
    payload: status,
  });

  const toggleAddNewPluginOfferingModal = (status: boolean) => ({
    type: TOGGLE_ADD_NEW_PLUGIN_OFFERING_MODAL,
    payload: status,
  });

  const toggleConnectPluginDetailModal = (status: boolean) => ({
    type: TOGGLE_CONNECT_PLUGIN_DETAIL_MODAL,
    payload: status,
  });

  return {
    activitiesRefresh,
    toggleAssignUserToPracticeModal,
    toggleConnectConfigModal,
    toggleEditConversionPimsModalOpen,
    toggleEditingPractice,
    toggleIsEditingPractice,
    toggleIsViewAddingNewCommunication,
    toggleAddNewPluginOfferingModal,
    toggleConnectPluginDetailModal,
    toggleLeadsGenerationConfirmationModal,
    toggleMutePracticeModal,
    togglePracticeScheduleModal,
    togglePracticeWorkflowModalOpen,
    toggleRelationsModalOpen,
    toggleResetConversionModalOpen,
    toggleSidebarOpen,
    toggleSidebarPracticeSearchOpen,
    toggleUpdateNotesModal,
    triggerPracticeRefresh,
    triggerRefresh,
  };
};
